'use client';
import { Check, Languages } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useTransition } from 'react';
import { Button, DropdownMenuContent, DropdownMenuItem, DropdownMenuRoot, DropdownMenuTrigger } from '@freealbum/ui';
import { usePathname, useRouter } from '@/i18n/navigation';
import { locales, type Locale } from '@/i18n/routing';
import { LOCALE_COOKIE, LOCALE_SOURCE_COOKIE } from '@/i18n/detect';
import { writeCookie } from '@/lib/cookies';

export function LanguageSwitcher({ compact }: { compact?: boolean }) {
  const t = useTranslations('language');
  const locale = useLocale();
  // next-intl's `usePathname` gives the current path without the locale prefix, dynamic segments
  // already filled in, so re-navigating with a different `locale` keeps the user where they are.
  const pathname = usePathname();
  const router = useRouter();
  const [pending, startTransition] = useTransition();

  const switchTo = (next: Locale) => {
    if (next === locale) return;
    // A language picked by hand outranks any country guess, on this device, for a year. next-intl
    // only records the cookie on full page loads, and this navigation is not one, so write it here.
    writeCookie(LOCALE_COOKIE, next);
    writeCookie(LOCALE_SOURCE_COOKIE, 'cookie');
    startTransition(() => {
      router.replace(pathname, { locale: next });
    });
  };

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" loading={pending} aria-label={t('label')}>
          <Languages aria-hidden className="size-4" />
          <span className={compact ? 'sr-only' : undefined}>{t(locale === 'he' ? 'he' : 'en')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" aria-label={t('label')}>
        {locales.map((l) => (
          <DropdownMenuItem
            key={l}
            onSelect={() => switchTo(l)}
            icon={l === locale ? <Check className="size-4 text-accent" /> : <span className="size-4" />}
          >
            {t(l)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
